/**
 * Infinite Scroll Component for Shopify Collection Pages
 * 瀑布流自动加载组件 - 用于商品分类页面
 * 
 * 使用方法：
 * 在 collection 模板中添加：
 * <infinite-scroll data-total-pages="{{ paginate.pages }}" data-current-page="{{ paginate.current_page }}">
 *   <div class="infinite-scroll__loading hidden">
 *     <div class="infinite-scroll__spinner"></div>
 *   </div>
 *   <div class="infinite-scroll__sentinel"></div>
 *   <div class="infinite-scroll__end hidden">已加载全部商品</div>
 * </infinite-scroll>
 */

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.dataset.totalPages) || 1;
    this.loading = false;
    this.threshold = parseInt(this.dataset.threshold) || 200; // 距离分页组件多少像素时触发加载（默认200px）
    this.productGrid = null;
    this.loadingIndicator = null;
    this.sentinel = null;
    this.endMessage = null;
    this.observer = null;
    this.enabled = true;
    this.debug = false; // 生产环境关闭调试
  }

  log(...args) {
    if (this.debug) {
      console.log('[InfiniteScroll]', ...args);
    }
  }

  connectedCallback() {
    this.log('组件初始化...');
    
    // 查找商品网格容器
    this.productGrid = document.getElementById('product-grid');
    this.loadingIndicator = this.querySelector('.infinite-scroll__loading');
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.endMessage = this.querySelector('.infinite-scroll__end');
    
    this.log('productGrid:', this.productGrid);
    this.log('totalPages:', this.totalPages, 'currentPage:', this.currentPage);
    
    // 如果只有一页或找不到商品网格，则不启用无限滚动
    if (!this.productGrid || this.totalPages <= 1) {
      this.enabled = false;
      this.log('无限滚动未启用 - productGrid:', !!this.productGrid, 'totalPages:', this.totalPages);
      return;
    }
    
    // 添加标记类
    document.body.classList.add('infinite-scroll-enabled');
    
    // 隐藏传统分页
    this.hidePagination();
    
    // 使用滚动事件监听
    this.setupScrollListener();
    
    // 监听筛选变化事件
    this.setupFilterListener();
    
    this.log('无限滚动已启用');
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }
    document.body.classList.remove('infinite-scroll-enabled');
  }

  /**
   * 设置滚动事件监听
   * 以 infinite-scroll 组件（原分页按钮位置）为基准，而不是页面底部
   */
  setupScrollListener() {
    let lastCall = 0;
    const delay = 100;
    const self = this;
    
    this.scrollHandler = function() {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      
      if (self.loading || !self.enabled || self.currentPage >= self.totalPages) return;
      
      // 获取 infinite-scroll 组件的位置（它在原分页按钮的位置）
      const componentRect = self.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // 计算组件顶部距离视口底部的距离
      const distanceToViewport = componentRect.top - windowHeight;
      
      // 当组件距离视口底部小于 threshold 时触发加载
      if (distanceToViewport <= self.threshold) {
        self.log('触发加载 - 距视口:', Math.round(distanceToViewport), 'px');
        self.loadNextPage();
      }
    };
    
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.log('滚动监听已设置，threshold:', this.threshold);
    
    // 立即检查一次，防止页面初始就在底部
    setTimeout(() => {
      this.scrollHandler();
    }, 500);
  }

  /**
   * 监听筛选变化，当筛选条件改变时重置无限滚动
   */
  setupFilterListener() {
    // 监听 popstate 事件（浏览器前进/后退）
    window.addEventListener('popstate', this.handleFilterChange.bind(this));
    
    // 创建 MutationObserver 监听 ProductGridContainer 的变化
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      this.gridObserver = new MutationObserver((mutations) => {
        // 当网格内容被替换时（通常是筛选操作），重置无限滚动
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.resetAfterFilter();
          }
        });
      });
      
      this.gridObserver.observe(productGridContainer, {
        childList: true,
        subtree: false
      });
    }
  }

  /**
   * 处理筛选变化
   */
  handleFilterChange() {
    // 延迟执行以确保DOM已更新
    setTimeout(() => {
      this.resetAfterFilter();
    }, 100);
  }

  /**
   * 筛选后重置无限滚动状态
   */
  resetAfterFilter() {
    // 重新查找商品网格（可能已被替换）
    this.productGrid = document.getElementById('product-grid');
    
    // 从URL获取当前页码
    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page')) || 1;
    
    // 尝试从新的分页元素获取总页数
    this.updateTotalPages();
    
    // 重置状态
    this.loading = false;
    this.enabled = this.totalPages > 1;
    
    // 重置UI
    if (this.endMessage) {
      this.endMessage.classList.add('hidden');
    }
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }
    
    // 隐藏分页
    this.hidePagination();
    
    // 重新设置观察器
    if (this.enabled) {
      this.setupObserver();
    }
  }

  /**
   * 更新总页数（从分页元素中获取）
   */
  updateTotalPages() {
    // 尝试从分页链接获取总页数
    const paginationItems = document.querySelectorAll('.pagination__item:not(.pagination__item-arrow)');
    if (paginationItems.length > 0) {
      const lastPageItem = paginationItems[paginationItems.length - 1];
      const lastPageNum = parseInt(lastPageItem.textContent);
      if (!isNaN(lastPageNum)) {
        this.totalPages = lastPageNum;
      }
    }
    
    // 更新 data 属性
    this.dataset.totalPages = this.totalPages;
    this.dataset.currentPage = this.currentPage;
  }

  /**
   * 隐藏传统分页组件
   */
  hidePagination() {
    const pagination = document.querySelector('.pagination-wrapper');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  /**
   * 加载下一页商品
   */
  async loadNextPage() {
    if (this.loading || this.currentPage >= this.totalPages) {
      this.log('跳过加载 - loading:', this.loading, 'currentPage:', this.currentPage, 'totalPages:', this.totalPages);
      return;
    }
    
    this.loading = true;
    this.showLoading();
    
    const nextPage = this.currentPage + 1;
    const url = this.buildUrl(nextPage);
    
    this.log('正在加载第', nextPage, '页，URL:', url);
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      this.log('获取到 HTML 响应，长度:', html.length);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // 提取新商品
      const newProductGrid = doc.getElementById('product-grid');
      const newProducts = newProductGrid ? newProductGrid.querySelectorAll('.grid__item') : [];
      
      this.log('找到新商品数量:', newProducts.length);
      
      if (newProducts.length > 0) {
        // 创建文档片段以提高性能
        const fragment = document.createDocumentFragment();
        
        newProducts.forEach((product) => {
          const clonedProduct = product.cloneNode(true);
          
          // 移除滚动动画触发器以避免重复动画问题
          clonedProduct.querySelectorAll('.scroll-trigger').forEach((el) => {
            el.classList.add('scroll-trigger--cancel');
          });
          
          fragment.appendChild(clonedProduct);
        });
        
        // 追加商品到现有网格
        this.productGrid.appendChild(fragment);
        
        this.currentPage = nextPage;
        this.log('已加载第', nextPage, '页，共', newProducts.length, '个商品');
        
        // 更新URL（不刷新页面）
        this.updateUrl(nextPage);
        
        // 触发自定义事件，供其他组件监听
        this.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', {
          bubbles: true,
          detail: {
            page: nextPage,
            totalPages: this.totalPages,
            newProductsCount: newProducts.length
          }
        }));
        
        // 初始化新加载商品的动画（如果有的话）
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger();
        }
      }
      
      // 检查是否还有更多页
      if (this.currentPage >= this.totalPages) {
        this.log('已加载全部页面');
        this.showEndMessage();
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
      }
    } catch (error) {
      console.error('Infinite scroll: Failed to load next page:', error);
      this.log('加载失败:', error.message);
      
      // 显示错误提示并允许重试
      this.showError();
    } finally {
      this.loading = false;
      this.hideLoading();
    }
  }

  /**
   * 构建请求URL
   * @param {number} page - 页码
   * @returns {string} - 完整URL
   */
  buildUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    
    // 添加 section_id 以获取部分渲染（Shopify Section Rendering API）
    const sectionId = this.productGrid?.dataset.id;
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }
    
    return url.toString();
  }

  /**
   * 更新浏览器URL
   * @param {number} page - 当前页码
   */
  updateUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    history.replaceState({ page, infiniteScroll: true }, '', url.toString());
  }

  /**
   * 显示加载指示器
   */
  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
    }
  }

  /**
   * 隐藏加载指示器
   */
  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }
  }

  /**
   * 显示结束提示
   */
  showEndMessage() {
    if (this.endMessage) {
      this.endMessage.classList.remove('hidden');
    }
  }

  /**
   * 显示错误提示
   */
  showError() {
    // 创建错误提示元素（如果不存在）
    let errorElement = this.querySelector('.infinite-scroll__error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'infinite-scroll__error';
      errorElement.innerHTML = `
        <p>加载失败，请重试</p>
        <button class="infinite-scroll__retry button button--secondary" type="button">重试</button>
      `;
      this.insertBefore(errorElement, this.sentinel);
      
      // 绑定重试按钮事件
      const retryButton = errorElement.querySelector('.infinite-scroll__retry');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          errorElement.remove();
          this.loadNextPage();
        });
      }
    }
    errorElement.classList.remove('hidden');
  }

  /**
   * 重置无限滚动状态（供外部调用）
   * @param {number} totalPages - 新的总页数
   */
  reset(totalPages = null) {
    this.log('重置无限滚动，新总页数:', totalPages);
    this.currentPage = 1;
    if (totalPages !== null) {
      this.totalPages = totalPages;
    }
    this.loading = false;
    this.enabled = this.totalPages > 1;
    
    // 重新获取商品网格引用
    this.productGrid = document.getElementById('product-grid');
    
    // 重置UI状态
    if (this.endMessage) {
      this.endMessage.classList.add('hidden');
    }
    
    const errorElement = this.querySelector('.infinite-scroll__error');
    if (errorElement) {
      errorElement.remove();
    }
    
    // 隐藏分页
    this.hidePagination();
    
    // 重新设置观察器和滚动监听
    if (this.enabled) {
      this.setupObserver();
      // 移除旧的滚动监听
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      this.setupScrollListener();
    }
  }
}

// 注册自定义元素
customElements.define('infinite-scroll', InfiniteScroll);

// 导出用于与 FacetFiltersForm 集成的重置函数
window.resetInfiniteScroll = function(totalPages = null) {
  const infiniteScroll = document.querySelector('infinite-scroll');
  if (infiniteScroll) {
    infiniteScroll.reset(totalPages);
  }
};